
import React, { useEffect, useState, useRef } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

interface ScriptEditorProps {
  script: string;
  onScriptChange: (script: string) => void;
}

const ScriptEditor = ({ script, onScriptChange }: ScriptEditorProps) => {
  const numberOfWords = script.trim().split(/\s+/).filter(Boolean).length;
  const [wordCount, setWordCount] = useState(numberOfWords);
  const [progress, setProgress] = useState(0);
  const [isGeneratingScript, setIsGeneratingScript] = useState(true);
  const [showLoadingProgressBar, setShowLoadingProgressBar] = useState(true);
  const progressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const MAX_WORDS = 150;
  const scriptStillLoading = !script && isGeneratingScript;
  const placeholderScript = '';

  useEffect(() => {
    if (script) {
      setWordCount(numberOfWords);
      setIsGeneratingScript(false);
      setShowLoadingProgressBar(false);
    }
  }, [script]);

  useEffect(() => {
    // Reset and start progress animation when script is generating
    if (isGeneratingScript) {
      setProgress(0);

      // Clear any existing timer
      if (progressTimerRef.current) {
        clearTimeout(progressTimerRef.current);
      }

      // Start the progress animation
      const startTime = Date.now();
      const totalDuration = 60000; // 60 seconds in milliseconds
      const fastPhaseDuration = 45000; // 45 seconds for 0-75%
      const slowPhaseDuration = 15000; // 15 seconds for 75-100%

      const updateProgress = () => {
        const elapsed = Date.now() - startTime;

        if (elapsed < fastPhaseDuration) {
          // Fast phase (0-75% in 45 seconds)
          const fastProgress = Math.min(75, (elapsed / fastPhaseDuration) * 75);

          setProgress(Math.floor(fastProgress));

          progressTimerRef.current = setTimeout(updateProgress, 100);
        } else if (elapsed < totalDuration) {
          // Slow phase (75-100% in 15 seconds)
          const slowPhaseElapsed = elapsed - fastPhaseDuration;
          const slowProgress = 75 + Math.min(25, (slowPhaseElapsed / slowPhaseDuration) * 25);

          setProgress(Math.floor(slowProgress));

          progressTimerRef.current = setTimeout(updateProgress, 100);
        } else {
          // Complete
          setProgress(100);
        }
      };

      progressTimerRef.current = setTimeout(updateProgress, 100);

      // Cleanup function
      return () => {
        if (progressTimerRef.current) {
          clearTimeout(progressTimerRef.current);
        }
      };
    }
  }, [isGeneratingScript]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newScript = e.target.value;

    // Count words
    const words = newScript.trim().split(/\s+/).filter(Boolean);
    setWordCount(words.length);

    // Pass script to parent component
    onScriptChange(newScript);
  };

  const getWordCountStatus = () => {
    if (wordCount <= MAX_WORDS * 0.5) return "text-green-500";
    if (wordCount <= MAX_WORDS * 0.75) return "text-amber-500";
    if (wordCount <= MAX_WORDS) return "text-orange-500";
    return "text-red-500";
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Your Script</h3>
        <Badge
          variant={wordCount > MAX_WORDS ? "destructive" : "outline"}
          className={wordCount > MAX_WORDS ? "" : getWordCountStatus()}
        >
          {wordCount}/{MAX_WORDS} words
        </Badge>
      </div>

      <div className="relative">
        {showLoadingProgressBar && (
          <div className="flex flex-col items-center justify-center min-h-[180px] bg-white/90 absolute w-full z-10">
            <progress value={progress} max={100} className="w-full mb-2 [&::-webkit-progress-bar]:rounded-lg [&::-webkit-progress-value]:rounded-lg   [&::-webkit-progress-bar]:bg-slate-300 [&::-webkit-progress-value]:bg-violet-600 [&::-moz-progress-bar]:bg-violet-600" />
            <p className="text-muted-foreground">Generating script</p>
            <p className="text-muted-foreground text-sm">This can take up to 60 seconds</p>
          </div>
        )}

        <Textarea
          disabled={scriptStillLoading}
          value={scriptStillLoading ? placeholderScript : script}
          onChange={handleChange}
          className={`min-h-[180px] font-medium ${wordCount > MAX_WORDS ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
          placeholder=""
        />
      </div>

      <p className="text-sm text-muted-foreground">
        Edit your script above. Keep it under 150 words for best results.
      </p>
    </div>
  );
};

export default ScriptEditor;
